import { z } from 'zod'

// Contract v1 for the consent feed (ADR 0034, communications plan §4.4):
// the explicit marketing opt-outs (and, where the app records them with
// evidence, the explicit grants) an app holds, so Hub's consent ledger can
// import them as evidence. Same auth, headers and rules as source-read.ts
// (per-app HUB_READ_SECRET as a bearer, constant-time compare, no-store,
// read-only, GET only).
//
//   GET <prefix>/consent?cursor=<c>&limit=<1..200>
//
// A page of consent facts, in the app's own stable order (oldest first by
// the time the fact happened where the app records one, then by id).
//
// Paging, as the organization feed (source-organizations.ts):
//   - `cursor` is opaque to Hub: whatever the app sent as `nextCursor`.
//   - `nextCursor` comes back on every page that has items, the last one
//     included. A page with no items echoes the cursor it was given (null
//     when it had none).
//   - `limit` defaults to 100, at most 200.
//   - 400 {error: 'invalid_cursor' | 'invalid_limit'}.
//   - Hub imports idempotently by (app, item id) and also re-reads the
//     whole feed from the start once a day, so an app whose rows carry no
//     time (or whose times can arrive out of order) is still caught up.
//
// The rules an app must keep (principle 1: consent is evidence, never
// inference):
//   - A `grant` only for an explicit, recorded opt-in. Never from a default,
//     a purchase, an account, or the absence of an opt-out.
//   - A `withdraw` for every explicit opt-out the app holds, including ones
//     later undone in the app (Hub keeps the withdrawal; an app's re-opt-in
//     is not evidence of consent unless it is itself an explicit grant).
//   - A `stop` for a phone number that replied STOP to the app's texts.
//   - `id` is stable: the same fact always has the same id, and a new fact
//     (an opt-out made again later) has a new one.
//   - `address` is the email or phone exactly as the app holds it, or null
//     when the app has none; Hub normalizes it. It is evidence, never an
//     identity key (spec §4 rule 7).
//   - `subject` is the person's BRE Auth subject only when the app knows it
//     exactly (an account the app signed in), never a guess from an address.
//   - `occurredAt` is ISO 8601, or null when the app never recorded when.
//   - Evidence is what the app recorded, verbatim; null when it recorded
//     nothing. Never reconstructed.

export const SOURCE_CONSENT_PATH = '/consent'
export const SOURCE_CONSENT_PAGE_SIZE = 100
export const SOURCE_CONSENT_MAX_LIMIT = 200

export const SOURCE_CONSENT_KINDS = ['grant', 'withdraw', 'stop'] as const
export type SourceConsentKind = (typeof SOURCE_CONSENT_KINDS)[number]
export const SOURCE_CONSENT_CHANNELS = ['email', 'sms'] as const
export type SourceConsentChannel = (typeof SOURCE_CONSENT_CHANNELS)[number]

export type SourceConsentItem = {
  id: string
  kind: SourceConsentKind
  channel: SourceConsentChannel
  address: string | null
  subject: string | null
  // The app's own word for what the fact covers, e.g. "FOLLOW_UP",
  // "marketing", "promotional". Hub maps it to a purpose (comms_import_scope);
  // a scope Hub doesn't map is recorded as skipped, never guessed.
  scope: string
  occurredAt: string | null
  // Who or what made it in the app, e.g. "customer", "staff", "sms_keyword",
  // "account_settings".
  sourceKind: string
  evidence: {
    // The exact wording the person was shown, and its version, as recorded.
    statement: string | null
    statementVersion: string | null
    // Anything else the app recorded (a form or page name, a channel).
    detail: string | null
  }
}

export type SourceConsentPage = {
  version: 1
  asOf: string
  items: SourceConsentItem[]
  nextCursor: string | null
  hasMore: boolean
}

const strOrNull = z.string().nullable()

export const SourceConsentItemSchema = z.object({
  id: z.string(),
  kind: z.enum(SOURCE_CONSENT_KINDS),
  channel: z.enum(SOURCE_CONSENT_CHANNELS),
  address: strOrNull,
  subject: strOrNull,
  scope: z.string(),
  occurredAt: strOrNull,
  sourceKind: z.string(),
  evidence: z.object({ statement: strOrNull, statementVersion: strOrNull, detail: strOrNull }),
})

export const SourceConsentPageSchema = z.object({
  version: z.literal(1),
  asOf: z.string(),
  items: z.array(SourceConsentItemSchema),
  nextCursor: strOrNull,
  hasMore: z.boolean(),
})

type Equal<A, B> = [A] extends [B] ? ([B] extends [A] ? true : false) : false
const same = <T extends true>(_: T) => _
same<Equal<z.infer<typeof SourceConsentPageSchema>, SourceConsentPage>>(true)
